import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { useFolderStore } from '../../store/folderStore';
import Modal from '../ui/Modal';
import Button from '../ui/Button';

interface DeleteFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folderId?: string;
  folderName?: string;
  hasSubfolders?: boolean;
  documentCount?: number;
  threadCount?: number;
  /** When provided, called instead of folder store delete (e.g. for KB/Global Docs). Caller handles API and refresh. */
  onDeleteConfirm?: (folderId: string, force: boolean) => Promise<void>;
}

const DeleteFolderModal: React.FC<DeleteFolderModalProps> = ({
  isOpen,
  onClose,
  folderId,
  folderName,
  hasSubfolders = false,
  documentCount = 0,
  threadCount = 0,
  onDeleteConfirm,
}) => {
  const { deleteFolder, isLoading: storeLoading } = useFolderStore();
  const [confirmed, setConfirmed] = React.useState(false);
  const [localLoading, setLocalLoading] = React.useState(false);

  const isLoading = onDeleteConfirm ? localLoading : storeLoading;

  const handleDelete = async () => {
    if (!folderId) return;
    try {
      if (onDeleteConfirm) {
        setLocalLoading(true);
        await onDeleteConfirm(folderId, true);
        onClose();
      } else {
        await deleteFolder(folderId, true);
        onClose();
      }
    } catch (error) {
      if (!onDeleteConfirm) {
        // Error is handled in the store (toast)
        console.error('Error deleting folder:', error);
      }
      throw error;
    } finally {
      setLocalLoading(false);
    }
  };

  const handleClose = () => {
    setConfirmed(false);
    onClose();
  };

  if (!folderId) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Delete Folder"
      maxWidth="md"
    >
      <div className="space-y-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-gray-900">
              Delete "{folderName}"?
            </h3>
            <div className="mt-2 space-y-2">
              <p className="text-sm text-gray-600">
                This action cannot be undone.
              </p>

              <div className="bg-yellow-50 border border-yellow-200 p-3 rounded">
                <p className="text-sm text-yellow-800 font-medium">
                  ⚠️ Warning: This folder may contain content
                </p>
                {(hasSubfolders || documentCount > 0 || threadCount > 0) ? (
                  <>
                    <p className="text-sm text-yellow-700 mt-1">
                      This folder contains:{' '}
                      {[
                        hasSubfolders && 'subfolders',
                        threadCount > 0 && `${threadCount} thread${threadCount !== 1 ? 's' : ''}`,
                        documentCount > 0 && `${documentCount} document${documentCount !== 1 ? 's' : ''}`
                      ].filter(Boolean).join(', ')}
                    </p>
                    <p className="text-sm text-yellow-700 mt-1">
                      {hasSubfolders
                        ? 'Deleting will also delete all subfolders and their contents.'
                        : `Deleting will affect ${[threadCount > 0 && `${threadCount} thread${threadCount !== 1 ? 's' : ''}`, documentCount > 0 && `${documentCount} document${documentCount !== 1 ? 's' : ''}`].filter(Boolean).join(' and ')} associated with it.`
                      }
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-yellow-700 mt-1">
                    The system will check if this folder contains any documents, threads, or subfolders before deletion.
                  </p>
                )}
                <div className="mt-3">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={confirmed}
                      onChange={(e) => setConfirmed(e.target.checked)}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded cursor-pointer"
                    />
                    <span className="text-sm text-yellow-800 cursor-pointer">
                      I understand and want to delete this folder{hasSubfolders ? ' and all its subfolders' : ''}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="danger"
            loading={isLoading}
            disabled={isLoading || !confirmed}
          >
            Delete Folder
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteFolderModal;
